import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorMode,
  Divider,
} from '@chakra-ui/react';
import { NavbarWithCallToAction } from '../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor, brandConfig } from '../brandConfig';

export const TermsOfUse = () => {
  const { colorMode } = useColorMode();
  const lastUpdated = "November 19, 2025";

  return (
    <>
      <NavbarWithCallToAction />
      <Box minH="100vh" bg={getColor('background.main', colorMode)} py={20}>
        <Container maxW="900px">
          <VStack spacing={8} align="start">
            <VStack spacing={2} align="start" w="full">
              <Text fontSize="sm" color={getColor('text.secondary', colorMode)}>
                Last updated: {lastUpdated}
              </Text>
              <Heading
                as="h1"
                size="2xl"
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                Terms of Use
              </Heading>
            </VStack>

            <Divider />

            <VStack spacing={6} align="start" w="full">
              <Box>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" fontWeight="medium" mb={4}>
                  PLEASE READ THESE TERMS OF USE CAREFULLY BEFORE USING THE WEBSITE.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  1. Acceptance of Terms
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  These Terms of Use govern your access to and use of {brandConfig.siteName} (the "Website").
                  By accessing or using the Website, you accept and agree to be bound by these Terms of Use
                  and our Privacy Policy. If you do not agree to these Terms of Use, you must not access or
                  use the Website.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  2. Who May Use the Website
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  The Website is offered and available to users who are 13 years of age or older. By using
                  the Website, you represent and warrant that you are of legal age to form a binding contract
                  and meet all eligibility requirements. If you do not meet these requirements, you must not
                  access or use the Website.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  3. Changes to Terms
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  We may revise and update these Terms of Use from time to time at our sole discretion. All
                  changes are effective immediately when posted. Your continued use of the Website following
                  the posting of revised Terms of Use means that you accept and agree to the changes.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  4. Intellectual Property Rights
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  The Website and its entire contents, features, and functionality are owned by {brandConfig.siteName},
                  its licensors, or other providers of such material and are protected by copyright, trademark,
                  and other intellectual property laws. You may not reproduce, distribute, modify, create
                  derivative works of, publicly display, or exploit for any commercial purposes any portion
                  of the Website without our express written permission.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  5. Prohibited Uses
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={3}>
                  You may use the Website only for lawful purposes and in accordance with these Terms. You agree not to use the Website:
                </Text>
                <VStack spacing={2} align="start" pl={6}>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • In any way that violates any applicable law or regulation
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • To exploit, harm, or attempt to exploit or harm minors in any way
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • To transmit any advertising or promotional material without our prior written consent
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • To impersonate or attempt to impersonate the company, its employees, or other users
                  </Text>
                  <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                    • To engage in any conduct that restricts or inhibits anyone's use of the Website
                  </Text>
                </VStack>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  6. User Accounts and Security
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  To access certain features of the Website, you may be required to register for an account.
                  You agree to provide accurate, current, and complete information during registration and to
                  update such information to keep it accurate, current, and complete. You are responsible for
                  safeguarding your account credentials and for any activities or actions under your account.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  7. Links to Third-Party Websites
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  The Website may contain links to third-party websites or services that are not owned or
                  controlled by us. We have no control over and assume no responsibility for the content,
                  privacy policies, or practices of any third-party websites or services. You acknowledge
                  and agree that we shall not be liable for any damage or loss caused by your use of any
                  third-party websites or services.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  8. Disclaimer of Warranties
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  THE WEBSITE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT ANY WARRANTIES OF
                  ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE WEBSITE WILL BE UNINTERRUPTED,
                  TIMELY, SECURE, OR ERROR-FREE. YOUR USE OF THE WEBSITE IS AT YOUR OWN RISK.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  9. Limitation of Liability
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  IN NO EVENT SHALL {brandConfig.siteName.toUpperCase()}, ITS AFFILIATES, LICENSORS, SERVICE PROVIDERS,
                  EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
                  DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE WEBSITE, WHETHER BASED ON WARRANTY, CONTRACT,
                  TORT, OR ANY OTHER LEGAL THEORY.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  10. Governing Law
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  These Terms of Use shall be governed by and construed in accordance with applicable laws,
                  without regard to conflict of law provisions. Any legal action or proceeding arising under
                  these Terms of Use will be brought exclusively in the appropriate courts.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  11. Changes to the Website
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  We may update the content on the Website from time to time, but the content is not necessarily
                  complete or up-to-date. We reserve the right to withdraw or amend the Website, and any service
                  or material we provide, without notice. We will not be liable if for any reason all or any part
                  of the Website is unavailable at any time.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  12. Contact Information
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  If you have any questions about these Terms of Use, please contact us at{' '}
                  {brandConfig.contact.email && (
                    <Text as="span" color={getColor('accent.primary', colorMode)} fontWeight="medium">
                      {brandConfig.contact.email}
                    </Text>
                  )}
                </Text>
              </Box>
            </VStack>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
    </>
  );
};

export default TermsOfUse;
